/**
 * apps/battle/client/src/BattleScreen.tsx — Question → answer → scored feedback.
 *
 * Per spec §7:
 *   - Stem + MCQ options (or textarea for free-text)
 *   - Submit → scored feedback panel: score, AO breakdown chips, chain/condition flags,
 *     feedback text, model answer on final attempt, XP burst animation
 *   - Back to map with updated progress on completion
 */
import React, { useState, useEffect } from 'react';
import { api, ApiError } from './api.js';
import type { Question, GradeResult, SubmitResp } from './api.js';
import { Spinner, ErrorBanner } from './widgets.js';
// Phase 4.2 M4 — student-facing Study drawer triggered by the [📖] button.
import { StudyDrawer } from './StudyDrawer.js';
import { badgeName } from './terminal/config.js';

interface BattleScreenProps {
  clusterId: string;
  battleType: string;
  retryTemplateId?: string; // FIX A6: optional pinned template from ReviewScreen "Retry to improve"
  onBack: (xpAwarded: number, newLevel: number, streakDays?: number) => void;
  // FIX 2a/2e: optional review entry point from the completion panel
  onReview?: (clusterId: string) => void;
}

interface XpBurstState {
  id: number;
  xp: number;
}

function AoChips({ result }: { result: GradeResult }) {
  const chips: { label: string; value: number | undefined }[] = [
    { label: 'AO1', value: result.ao1 },
    { label: 'AO2', value: result.ao2 },
    { label: 'AO3', value: result.ao3 },
    { label: 'AO4', value: result.ao4 },
  ].filter((c) => c.value !== undefined);

  if (chips.length === 0) return null;

  return (
    <div className="ao-chips mt-3">
      {chips.map((c) => (
        <span key={c.label} className="ao-chip">
          {c.label}: {c.value}
        </span>
      ))}
      {(result.ao2Level ?? result.ao3Level ?? result.ao4Level) !== undefined && (
        <span className="ao-chip" style={{ color: 'var(--violet)' }}>
          L{result.ao2Level ?? result.ao3Level ?? result.ao4Level}
        </span>
      )}
      {(result.chainStepsDetected ?? 0) > 0 && (
        <span className="ao-chip" style={{ color: 'var(--success)' }}>
          ⛓ Chain×{result.chainStepsDetected}
        </span>
      )}
      {result.conditionStmtPresent && (
        <span className="ao-chip" style={{ color: 'var(--warning)' }}>
          ⚖ Conditional
        </span>
      )}
    </div>
  );
}

export function BattleScreen({ clusterId, battleType, retryTemplateId: initialRetryTemplateId, onBack, onReview }: BattleScreenProps) {
  const [question, setQuestion] = useState<Question | null>(null);
  const [attemptNumber, setAttemptNumber] = useState(1);
  const [maxAttempts, setMaxAttempts] = useState(3);
  const [clusterProgress, setClusterProgress] = useState<{ total: number; cleared: number; remaining: number } | null>(null);
  const [answer, setAnswer] = useState('');
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitResp, setSubmitResp] = useState<SubmitResp | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [exhausted, setExhausted] = useState(false); // FIX 2a: cluster_exhausted completion state
  const [xpBursts, setXpBursts] = useState<XpBurstState[]>([]);
  const [showLevelUp, setShowLevelUp] = useState(false);
  const [levelUpText, setLevelUpText] = useState('');
  const burstCounter = React.useRef(0);
  // Phase 4.2 M4: Study drawer open state.
  const [studyOpen, setStudyOpen] = useState(false);

  useEffect(() => {
    // FIX A6: if an initial retryTemplateId is provided (from ReviewScreen), pin it on first load
    loadQuestion(initialRetryTemplateId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clusterId, battleType]);

  // Close the Study drawer whenever the underlying question changes
  // (next/retry/load) so stale notes don't linger.
  useEffect(() => {
    setStudyOpen(false);
  }, [question?.id]);

  async function loadQuestion(retryTemplateId?: string) {
    setLoading(true);
    setError(null);
    setExhausted(false);
    setQuestion(null);
    setSubmitResp(null);
    setAnswer('');
    setSelectedOption(null);
    try {
      const qs = new URLSearchParams({ cluster: clusterId, type: battleType });
      // Retry pins the same question; a fresh load rotates to the next question in the cluster.
      if (retryTemplateId) qs.set('retry', retryTemplateId);
      const resp = await api.get<{ question: Question; attemptNumber: number; maxAttempts: number; clusterProgress?: { total: number; cleared: number; remaining: number } }>(
        `/api/battle/next?${qs.toString()}`,
      );
      setQuestion(resp.question);
      setAttemptNumber(resp.attemptNumber);
      setMaxAttempts(resp.maxAttempts);
      if (resp.clusterProgress) setClusterProgress(resp.clusterProgress);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        // No questions yet — informational, neutral message
        setError('No questions available for this battle type in this cluster yet. Try another!');
      } else if (err instanceof ApiError && err.status === 409) {
        // FIX 2a: cluster exhausted — celebratory completion state, not an error
        setExhausted(true);
      } else {
        setError('Failed to load question. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleSubmit() {
    if (!question) return;
    const ans = question.options ? (selectedOption ?? '') : answer.trim();
    if (!ans) return;

    setSubmitting(true);
    setError(null);
    try {
      const resp = await api.post<SubmitResp>('/api/battle/submit', {
        templateId: question.id,
        answer: ans,
        timeOnQuestionS: 30, // approximate
      });
      setSubmitResp(resp);

      // XP burst animation.
      if (resp.xpAwarded > 0) {
        const id = ++burstCounter.current;
        setXpBursts((prev) => [...prev, { id, xp: resp.xpAwarded }]);
        setTimeout(() => setXpBursts((prev) => prev.filter((b) => b.id !== id)), 1300);
      }

      // Level-up banner.
      if (resp.newLevel > resp.previousLevel) {
        setLevelUpText(`Level Up! → Lv ${resp.newLevel} 🎉`);
        setShowLevelUp(true);
        setTimeout(() => setShowLevelUp(false), 2200);
      }

      setAttemptNumber(resp.attemptNumber);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <Spinner size={28} />
      </div>
    );
  }

  // FIX 2a: Celebratory completion panel when all attempts are used (cluster_exhausted)
  if (exhausted && !question) {
    return (
      <div style={{ padding: '1.25rem', maxWidth: 480, margin: '0 auto' }}>
        <div className="completion-panel" data-testid="completion-panel">
          <div className="completion-panel-title">🎉 Cluster complete!</div>
          <div className="completion-panel-subtitle">
            You've cleared every question in this cluster. The next cluster is now unlocked.
          </div>
          <div className="completion-panel-actions">
            {onReview && (
              <button
                className="btn btn-primary"
                data-testid="btn-review-answers"
                onClick={() => onReview(clusterId)}
              >
                Review my answers
              </button>
            )}
            <button
              className="btn btn-secondary"
              data-testid="btn-back-to-map"
              onClick={() => onBack(0, 1)}
            >
              ← Back to Map
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (error && !question) {
    return (
      <div style={{ padding: '1.25rem' }}>
        <ErrorBanner msg={error} />
        <button className="btn btn-secondary mt-4" onClick={() => onBack(0, 1)}>
          ← Back to Map
        </button>
      </div>
    );
  }

  const isMcq = !!question?.options?.length;
  const result = submitResp?.gradeResult;
  const canRetry = submitResp && !submitResp.isFinalAttempt && !submitResp.gradeResult.cleared;

  return (
    <div style={{ padding: '1rem', maxWidth: 640, margin: '0 auto' }}>
      {/* XP bursts */}
      {xpBursts.map((b) => (
        <div key={b.id} className="xp-burst">+{b.xp} XP</div>
      ))}
      {showLevelUp && <div className="levelup-banner">{levelUpText}</div>}

      {/* Header */}
      <div className="flex items-center justify-between mt-2 mb-4">
        <button className="btn btn-secondary" onClick={() => onBack(submitResp?.xpAwarded ?? 0, submitResp?.newLevel ?? 1)}>
          ← Map
        </button>
        <div className="text-sm text-muted">
          {clusterId} · {battleType} · {question?.markValue ?? '-'} mark{(question?.markValue ?? 1) > 1 ? 's' : ''} · Attempt {attemptNumber}/{maxAttempts}
          {clusterProgress && (
            <span style={{ marginLeft: '0.5rem', color: 'var(--accent-light)' }}>
              · {clusterProgress.total} total · {clusterProgress.cleared} cleared · {clusterProgress.remaining} remaining
            </span>
          )}
        </div>
      </div>

      {question && (
        <div className="card">
          <p style={{ color: 'var(--text)', marginBottom: '1rem', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
            {question.stem}
          </p>

          {/* Answer area */}
          {!result && (
            <>
              {isMcq ? (
                <div className="mcq-options">
                  {question.options!.map((opt) => (
                    <button
                      key={opt.option}
                      className={`mcq-option ${selectedOption === opt.option ? 'selected' : ''}`}
                      onClick={() => setSelectedOption(opt.option)}
                      disabled={submitting}
                    >
                      <strong>{opt.option}.</strong> {opt.text}
                    </button>
                  ))}
                </div>
              ) : (
                <textarea
                  value={answer}
                  onChange={(e) => setAnswer(e.target.value)}
                  placeholder="Write your answer here…"
                  disabled={submitting}
                  style={{ marginBottom: '0.75rem' }}
                />
              )}
              {error && <ErrorBanner msg={error} />}
              <div className="flex items-center gap-2 mt-3">
                <button
                  className="btn btn-primary"
                  style={{ flex: 1 }}
                  data-testid="button-submit"
                  onClick={handleSubmit}
                  disabled={submitting || (isMcq ? !selectedOption : !answer.trim())}
                >
                  {submitting ? <Spinner size={16} /> : 'Submit Answer'}
                </button>
                {/* Phase 4.2 M4 — Study button. Disabled when no content available. */}
                <button
                  type="button"
                  className="btn btn-secondary btn-study"
                  aria-label="Open study notes"
                  title={
                    question?.hasStudyContent
                      ? 'Open study notes'
                      : 'No study notes for this question yet'
                  }
                  onClick={() => setStudyOpen(true)}
                  disabled={!question?.hasStudyContent || submitting}
                  data-testid="button-study-open"
                >
                  <span aria-hidden>📖</span>
                </button>
              </div>
            </>
          )}

          {/* Feedback panel */}
          {result && (
            <div className="feedback-panel">
              <div className="flex items-center gap-2">
                <span className={`score-badge ${result.cleared ? 'cleared' : 'failed'}`}>
                  {result.score}/{result.marks}
                </span>
                {result.cleared && <span style={{ color: 'var(--success)', fontWeight: 700 }}>Cleared! ✓</span>}
                {result.perfectStrike && (
                  <span style={{ color: 'var(--violet)', fontWeight: 700 }}>Perfect Strike ⭐</span>
                )}
                {submitResp.xpAwarded > 0 && (
                  <span style={{ color: 'var(--accent-light)', fontWeight: 700, marginLeft: 'auto' }}>
                    +{submitResp.xpAwarded} XP
                  </span>
                )}
              </div>

              <AoChips result={result} />

              <p style={{ marginTop: '0.875rem', color: 'var(--text)', lineHeight: 1.6 }}>
                {result.feedback}
              </p>

              {result.modelAnswer && (
                <div className="model-answer-box">
                  <div style={{ fontWeight: 600, marginBottom: '0.375rem', color: 'var(--accent-light)' }}>
                    Model Answer
                  </div>
                  <p style={{ color: 'var(--text)', whiteSpace: 'pre-wrap' }}>{result.modelAnswer}</p>
                </div>
              )}

              {submitResp.newBadges.length > 0 && (
                <div style={{ marginTop: '0.875rem', padding: '0.75rem', background: 'rgba(139,92,246,.12)', borderRadius: 8, border: '1px solid rgba(139,92,246,.3)' }}>
                  <div style={{ fontWeight: 700, color: 'var(--violet)' }}>🏅 Badge Unlocked!</div>
                  {submitResp.newBadges.map((b) => (
                    <div key={b} style={{ color: 'var(--text)', fontSize: '0.875rem' }}>{badgeName(b)}</div>
                  ))}
                </div>
              )}

              <div className="flex gap-2 mt-4">
                {canRetry && (
                  <button
                    className="btn btn-secondary"
                    data-testid="button-retry"
                    onClick={() => loadQuestion(question?.id)}
                  >
                    Retry ({maxAttempts - attemptNumber} left)
                  </button>
                )}
                <button
                  className="btn btn-primary"
                  data-testid="button-next-question"
                  onClick={() => loadQuestion()}
                >
                  Next question →
                </button>
                <button
                  className="btn btn-secondary"
                  data-testid="button-back"
                  onClick={() => onBack(submitResp.xpAwarded, submitResp.newLevel)}
                >
                  ← Back to Map
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Phase 4.2 M4 — Study drawer. Renders nothing when closed. */}
      <StudyDrawer
        open={studyOpen}
        conceptCodes={question?.conceptTags ?? []}
        onClose={() => setStudyOpen(false)}
      />
    </div>
  );
}
